import clsx from "clsx";
import { Fragment, useMemo, useState, type ReactNode } from "react";
import { describeError } from "@/api/client";
import { useDelayedVisibility } from "@/lib/hooks";
import { Button } from "../Button/Button";
import { EmptyState } from "../EmptyState/EmptyState";
import { Icon, type IconName } from "../Icon/Icon";
import { IconButton } from "../IconButton/IconButton";
import { InlineAlert } from "../InlineAlert/InlineAlert";
import { Menu } from "../Menu/Menu";
import { Skeleton } from "../Skeleton/Skeleton";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden";
import styles from "./DataTable.module.css";

export interface DataTableColumn<Row> {
  id: string;
  header: ReactNode;
  /** Accessible header text when `header` is an icon or visually empty. */
  headerLabel?: string;
  cell: (row: Row, rowIndex: number) => ReactNode;
  /** Quantities, prices, counts: right-aligned with tabular figures, never wrapped (header aligns too). */
  numeric?: boolean;
  align?: "start" | "center" | "end";
  /** Monospace with wide tracking (lot codes, CAS numbers, formula codes). */
  mono?: boolean;
  /** Clip long text with an ellipsis; wrap the cell content in a Tooltip for the full value. */
  truncate?: boolean;
  /** "fit" shrinks to the content (codes, badges); "wide" takes the spare space (names). */
  width?: "auto" | "fit" | "wide";
  /** Shows a sort button in the header. */
  sortable?: boolean;
  /** Sort key for client-side sorting (uncontrolled mode). */
  sortValue?: (row: Row) => string | number | null | undefined;
}

export interface SortState {
  columnId: string;
  direction: "asc" | "desc";
}

export interface RowAction<Row> {
  /** Ends in "…" when it opens a further step ("Rename…"). */
  label: string;
  onSelect: (row: Row) => void;
  icon?: IconName;
  /** Destructive actions are listed last, after a separator, in --color-danger. */
  danger?: boolean;
  disabled?: boolean;
}

export interface DataTableProps<Row> {
  columns: ReadonlyArray<DataTableColumn<Row>>;
  /** Undefined while the first load is pending. */
  rows: readonly Row[] | undefined;
  getRowId: (row: Row) => string;
  /** Names the table for screen readers ("Lots in stock"); visually hidden unless showCaption. */
  caption: string;
  showCaption?: boolean;
  loading?: boolean;
  /** A load error: shown in place of rows with a Retry button when onRetry is given. */
  error?: unknown;
  onRetry?: () => void;
  /** What belongs here and the action that fills it (an EmptyState with variant="inline"). */
  empty?: ReactNode;
  /** Controlled sorting (server-side or kept in the URL): you sort the rows. */
  sort?: SortState | null;
  onSortChange?: (sort: SortState | null) => void;
  /** Uncontrolled client-side sorting with each column's sortValue. */
  defaultSort?: SortState;
  rowActions?: (row: Row) => ReadonlyArray<RowAction<Row>>;
  /** Accessible name for a row's action menu ("Actions for lot T030-24S"). */
  rowActionsLabel?: (row: Row) => string;
  selectedRowId?: string | null;
  /** A class from your feature's CSS Module for a row (e.g. styles.depleted). */
  rowClassName?: (row: Row) => string | undefined;
  /** Keep the header visible while the table body scrolls (the table gets a viewport-high scroll area). */
  stickyHeader?: boolean;
  stickyFirstColumn?: boolean;
  skeletonRows?: number;
  /** Totals rows, rendered in <tfoot> (use <tr>/<td> with your own module classes). */
  footer?: ReactNode;
  className?: string;
}

type Comparable = string | number | null | undefined;

function compareValues(a: Comparable, b: Comparable): number {
  if (a === b) return 0;
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function nextSort(current: SortState | null | undefined, columnId: string): SortState | null {
  if (!current || current.columnId !== columnId) return { columnId, direction: "asc" };
  return current.direction === "asc" ? { columnId, direction: "desc" } : null;
}

const SORT_ICONS: Record<"asc" | "desc" | "none", IconName> = {
  asc: "arrowUp",
  desc: "arrowDown",
  none: "arrowUpDown",
};

/**
 * The shared data table: semantic <table> markup with sortable headers, delayed skeleton loading,
 * empty and error states, row action menus and right-aligned numeric columns.
 */
export function DataTable<Row>({
  columns,
  rows,
  getRowId,
  caption,
  showCaption = false,
  loading = false,
  error,
  onRetry,
  empty,
  sort,
  onSortChange,
  defaultSort,
  rowActions,
  rowActionsLabel = () => "Row actions",
  selectedRowId,
  rowClassName,
  stickyHeader = false,
  stickyFirstColumn = false,
  skeletonRows = 5,
  footer,
  className,
}: DataTableProps<Row>) {
  const controlled = onSortChange !== undefined;
  const [localSort, setLocalSort] = useState<SortState | null>(defaultSort ?? null);
  const activeSort = controlled ? (sort ?? null) : localSort;

  const sortedRows = useMemo(() => {
    if (!rows || controlled || !activeSort) return rows;
    const column = columns.find((candidate) => candidate.id === activeSort.columnId);
    if (!column?.sortValue) return rows;
    const factor = activeSort.direction === "asc" ? 1 : -1;
    const sortValue = column.sortValue;
    return [...rows].sort((a, b) => factor * compareValues(sortValue(a), sortValue(b)));
  }, [rows, controlled, activeSort, columns]);

  const hasRows = (sortedRows?.length ?? 0) > 0;
  const waitingForFirstLoad = loading && !hasRows;
  const showSkeleton = useDelayedVisibility(waitingForFirstLoad);
  const columnCount = columns.length + (rowActions ? 1 : 0);

  function handleSort(columnId: string) {
    const next = nextSort(activeSort, columnId);
    if (controlled) onSortChange(next);
    else setLocalSort(next);
  }

  let body: ReactNode;
  if (error && !hasRows) {
    body = (
      <tr>
        <td colSpan={columnCount} className={styles.stateCell}>
          <InlineAlert
            tone="danger"
            title="Couldn't load this list"
            action={onRetry ? <Button onClick={onRetry}>Retry</Button> : undefined}
          >
            {describeError(error)}
          </InlineAlert>
        </td>
      </tr>
    );
  } else if (waitingForFirstLoad) {
    body = showSkeleton ? (
      Array.from({ length: skeletonRows }, (_, rowIndex) => (
        <tr key={`skeleton-${rowIndex}`} className={styles.row} data-skeleton="">
          {Array.from({ length: columnCount }, (_, columnIndex) => (
            <td key={columnIndex} className={styles.cell}>
              <Skeleton width={columnIndex === 0 ? "3/4" : "1/2"} />
            </td>
          ))}
        </tr>
      ))
    ) : (
      <tr>
        <td colSpan={columnCount} className={styles.pendingCell} />
      </tr>
    );
  } else if (!hasRows) {
    body = (
      <tr>
        <td colSpan={columnCount} className={styles.stateCell}>
          {empty ?? <EmptyState variant="inline" headingLevel={3} title="Nothing here yet" />}
        </td>
      </tr>
    );
  } else {
    body = sortedRows?.map((row, rowIndex) => {
      const rowId = getRowId(row);
      const actions = rowActions?.(row) ?? [];
      const firstDanger = actions.findIndex((action) => action.danger);
      return (
        <tr
          key={rowId}
          className={clsx(styles.row, rowClassName?.(row))}
          data-selected={selectedRowId === rowId ? "" : undefined}
        >
          {columns.map((column) => (
            <td
              key={column.id}
              className={styles.cell}
              data-numeric={column.numeric ? "" : undefined}
              data-align={column.align}
              data-mono={column.mono ? "" : undefined}
              data-truncate={column.truncate ? "" : undefined}
              data-width={column.width}
            >
              {column.truncate ? (
                <span className={styles.truncate}>{column.cell(row, rowIndex)}</span>
              ) : (
                column.cell(row, rowIndex)
              )}
            </td>
          ))}
          {rowActions ? (
            <td className={clsx(styles.cell, styles.actionsCell)}>
              {actions.length ? (
                <Menu.Root>
                  <Menu.Trigger>
                    <IconButton icon="moreHorizontal" label={rowActionsLabel(row)} size="sm" tooltip={false} />
                  </Menu.Trigger>
                  <Menu.Popup align="end">
                    {actions.map((action, actionIndex) => (
                      <Fragment key={action.label}>
                        {actionIndex === firstDanger && actionIndex > 0 ? <Menu.Separator /> : null}
                        <Menu.Item
                          icon={action.icon}
                          danger={action.danger}
                          disabled={action.disabled}
                          onClick={() => action.onSelect(row)}
                        >
                          {action.label}
                        </Menu.Item>
                      </Fragment>
                    ))}
                  </Menu.Popup>
                </Menu.Root>
              ) : null}
            </td>
          ) : null}
        </tr>
      );
    });
  }

  return (
    <div
      className={clsx(styles.scroller, className)}
      data-sticky-header={stickyHeader ? "" : undefined}
      data-sticky-first-column={stickyFirstColumn ? "" : undefined}
      aria-busy={loading || undefined}
    >
      <table className={styles.table}>
        <caption className={showCaption ? styles.caption : styles.hiddenCaption}>{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => {
              const direction = activeSort?.columnId === column.id ? activeSort.direction : null;
              return (
                <th
                  key={column.id}
                  scope="col"
                  className={styles.headerCell}
                  data-numeric={column.numeric ? "" : undefined}
                  data-align={column.align}
                  data-width={column.width}
                  aria-sort={
                    column.sortable
                      ? direction === "asc"
                        ? "ascending"
                        : direction === "desc"
                          ? "descending"
                          : "none"
                      : undefined
                  }
                >
                  {column.sortable ? (
                    <button type="button" className={styles.sortButton} onClick={() => handleSort(column.id)}>
                      <span>{column.header}</span>
                      {column.headerLabel ? <VisuallyHidden>{column.headerLabel}</VisuallyHidden> : null}
                      <span className={styles.sortIcon} data-active={direction ? "" : undefined}>
                        <Icon name={SORT_ICONS[direction ?? "none"]} />
                      </span>
                    </button>
                  ) : (
                    <>
                      {column.header}
                      {column.headerLabel ? <VisuallyHidden>{column.headerLabel}</VisuallyHidden> : null}
                    </>
                  )}
                </th>
              );
            })}
            {rowActions ? (
              <th scope="col" className={clsx(styles.headerCell, styles.actionsCell)}>
                <VisuallyHidden>Actions</VisuallyHidden>
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>{body}</tbody>
        {footer && hasRows ? <tfoot className={styles.footer}>{footer}</tfoot> : null}
      </table>
    </div>
  );
}
